import { useMemo, useState } from 'react';
import { t } from '../i18n/index.ts';
import { money } from '../lib/format.ts';
import { comboGroups, priceNow, useImageUrl } from '../lib/menu.ts';
import type { Catalog } from '../lib/types.ts';
import { Icon } from './Icon.tsx';
import { Button, Dialog, Field, IconButton } from './ui.tsx';

export interface ComboPick {
  group: string;
  product: string;
  modifiers: string[];
}

/**
 * Putting a combo menu together: one choice (or as many as the group asks) per group, and
 * each chosen item's own options. A group with nothing to choose is shown as included.
 */
export function ComboDialog({
  catalog,
  comboId,
  onClose,
  onAdd,
}: {
  catalog: Catalog;
  comboId: string;
  onClose: () => void;
  onAdd: (c: { qty: number; note?: string; picks: ComboPick[]; total: number }) => void;
}) {
  const combo = catalog.products.find((p) => p.id === comboId)!;
  const groups = useMemo(() => comboGroups(catalog, comboId), [catalog, comboId]);
  const imageUrl = useImageUrl();
  const modGroupsOf = (productId: string) =>
    catalog.product_modifier_groups
      .filter((l) => l.product === productId)
      .sort((a, b) => a.sequence - b.sequence)
      .map((l) => catalog.modifier_groups.find((g) => g.id === l.group)!)
      .filter(Boolean);
  const defaultMods = (productId: string) =>
    modGroupsOf(productId).flatMap((g) => (g.min_select > 0 ? catalog.modifiers.filter((m) => m.group === g.id).slice(0, g.min_select).map((m) => m.id) : []));

  // Groups that offer exactly what they ask for need no choice: those items are simply in the menu.
  const [picked, setPicked] = useState<Record<string, string[]>>(() =>
    Object.fromEntries(
      groups.map((g) => {
        const open = g.items.filter((i) => i.product.available);
        return [g.id, open.length <= g.choose ? open.map((i) => i.product.id) : []];
      }),
    ),
  );
  const [mods, setMods] = useState<Record<string, string[]>>(() =>
    Object.fromEntries(groups.flatMap((g) => g.items.map((i) => [`${g.id}:${i.product.id}`, defaultMods(i.product.id)]))),
  );
  const [count, setCount] = useState(1);
  const [note, setNote] = useState('');

  const need = (g: (typeof groups)[number]) => Math.min(g.choose, g.items.length);
  const pick = (groupId: string, productId: string, choose: number) =>
    setPicked((p) => {
      const now = p[groupId] ?? [];
      if (now.includes(productId)) return { ...p, [groupId]: now.filter((x) => x !== productId) };
      if (choose === 1) return { ...p, [groupId]: [productId] };
      if (now.length >= choose) return p;
      return { ...p, [groupId]: [...now, productId] };
    });
  const toggleMod = (key: string, groupId: string, modId: string, max: number) =>
    setMods((all) => {
      const c = all[key] ?? [];
      if (c.includes(modId)) return { ...all, [key]: c.filter((x) => x !== modId) };
      const inGroup = c.filter((x) => catalog.modifiers.find((m) => m.id === x)?.group === groupId);
      if (max === 1) return { ...all, [key]: [...c.filter((x) => !inGroup.includes(x)), modId] };
      if (max > 0 && inGroup.length >= max) return all;
      return { ...all, [key]: [...c, modId] };
    });

  const base = priceNow(catalog, combo, new Date());
  let perMenu = base.price;
  const picks: ComboPick[] = [];
  for (const g of groups) {
    for (const pid of picked[g.id] ?? []) {
      const item = g.items.find((i) => i.product.id === pid)!;
      const chosen = mods[`${g.id}:${pid}`] ?? [];
      perMenu += item.price_delta + chosen.reduce((s, id) => s + (catalog.modifiers.find((m) => m.id === id)?.price_delta ?? 0), 0);
      picks.push({ group: g.id, product: pid, modifiers: chosen });
    }
  }
  const missingGroup = groups.find((g) => (picked[g.id] ?? []).length < need(g));
  const missingMods = picks.find((p) =>
    modGroupsOf(p.product).some((g) => p.modifiers.filter((x) => catalog.modifiers.find((m) => m.id === x)?.group === g.id).length < g.min_select),
  );
  const missing = missingGroup
    ? need(missingGroup) === 1
      ? t('combo.choose_one', { group: missingGroup.name })
      : t('combo.choose_n', { n: need(missingGroup), group: missingGroup.name })
    : missingMods
      ? t('combo.choose_options', { name: catalog.products.find((p) => p.id === missingMods.product)?.name ?? '' })
      : null;

  return (
    <Dialog
      title={combo.name}
      onClose={onClose}
      footer={
        <>
          <div className="stepper" aria-label={t('common.quantity')}>
            <IconButton icon="minus" label={t('common.less')} onClick={() => setCount((n) => Math.max(1, n - 1))} />
            <span className="stepper-value">{count}</span>
            <IconButton icon="plus" label={t('common.more')} onClick={() => setCount((n) => Math.min(99, n + 1))} />
          </div>
          <Button
            variant="primary"
            size="lg"
            icon="plus"
            disabled={!!missing}
            title={missing ?? undefined}
            onClick={() => onAdd({ qty: count, note: note.trim() || undefined, picks, total: perMenu * count })}
          >
            {t('options.add', { price: money(perMenu * count) })}
          </Button>
        </>
      }
    >
      <p className="combo-price">
        {base.rule && base.price < combo.price ? (
          <>
            <s className="muted">{money(combo.price)}</s> <strong>{money(base.price)}</strong>{' '}
            <span className="line-deal">
              <Icon name="clock" size={12} /> {base.rule}
            </span>
          </>
        ) : (
          <strong>{money(base.price)}</strong>
        )}
        {combo.description ? <span className="muted"> · {combo.description}</span> : null}
      </p>
      {groups.length === 0 ? <p className="callout callout-warn">{t('combo.not_set_up')}</p> : null}
      {groups.map((g) => {
        const choices = picked[g.id] ?? [];
        const fixed = g.items.length <= g.choose;
        return (
          <fieldset key={g.id} className="option-group">
            <legend>
              {g.name}{' '}
              <span className="muted">
                {fixed ? t('combo.included') : need(g) === 1 ? t('combo.pick_one') : t('combo.pick_n', { n: need(g), done: choices.length })}
              </span>
            </legend>
            <div className="option-list combo-list">
              {g.items.map((i) => {
                const on = choices.includes(i.product.id);
                const src = imageUrl(i.product);
                return (
                  <button
                    key={i.product.id}
                    role={g.choose === 1 ? 'radio' : 'checkbox'}
                    aria-checked={on}
                    className={`option combo-option${on ? ' active' : ''}`}
                    disabled={!i.product.available || fixed}
                    onClick={() => pick(g.id, i.product.id, g.choose)}
                  >
                    <span className="option-check">{on ? <Icon name="check" size={16} /> : null}</span>
                    {src ? <img className="combo-thumb" src={src} alt="" loading="lazy" /> : null}
                    <span className="option-name">
                      {i.product.name}
                      {!i.product.available ? <span className="muted"> · {t('product.sold_out')}</span> : null}
                    </span>
                    {i.price_delta ? <span className="option-price">{money(i.price_delta, { sign: true })}</span> : null}
                  </button>
                );
              })}
            </div>
            {choices.map((pid) => {
              const mg = modGroupsOf(pid);
              if (!mg.length) return null;
              const key = `${g.id}:${pid}`;
              const name = catalog.products.find((p) => p.id === pid)?.name ?? '';
              return (
                <div key={pid} className="combo-mods">
                  {mg.map((m) => (
                    <div key={m.id} className="combo-mod-group">
                      <span className="combo-mod-label">
                        {name} · {m.name} <span className="muted">{m.min_select > 0 ? t('options.required') : t('options.optional')}</span>
                      </span>
                      <div className="chips">
                        {catalog.modifiers
                          .filter((x) => x.group === m.id)
                          .map((x) => {
                            const on = (mods[key] ?? []).includes(x.id);
                            return (
                              <button
                                key={x.id}
                                role={m.max_select === 1 ? 'radio' : 'checkbox'}
                                aria-checked={on}
                                className={`chip${on ? ' active' : ''}`}
                                onClick={() => toggleMod(key, m.id, x.id, m.max_select)}
                              >
                                {on ? <Icon name="check" size={14} /> : null} {x.name}
                                {x.price_delta ? ` ${money(x.price_delta, { sign: true })}` : ''}
                              </button>
                            );
                          })}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </fieldset>
        );
      })}
      <Field label={t('options.note')} hint={t('options.note_hint')}>
        <input className="input" value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} />
      </Field>
      {missing ? (
        <p className="callout callout-warn" role="status">
          <Icon name="alert" size={16} /> {missing}
        </p>
      ) : null}
    </Dialog>
  );
}
